import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Driver, DriversListResponse } from './types';
import type { DriverFormInput } from './schemas';

interface ListParams {
  page?: number;
  limit?: number;
  q?: string;
  active?: 'true' | 'false';
  sort?: string;
}

const KEY = {
  all: ['drivers'] as const,
  list: (params: ListParams) => ['drivers', 'list', params] as const,
  one: (id: string) => ['drivers', 'one', id] as const,
};

export function useDriversList(params: ListParams = {}) {
  return useQuery<DriversListResponse>({
    queryKey: KEY.list(params),
    queryFn: async () => {
      const { data } = await api.get<DriversListResponse>('/drivers', { params });
      return data;
    },
  });
}

function toApiPayload(input: DriverFormInput, isCreate: boolean) {
  return {
    name: input.name,
    cpf: input.cpf,
    rg: input.rg || undefined,
    cnh: input.cnh,
    cnhCategory: input.cnhCategory,
    cnhExpiresAt: input.cnhExpiresAt,
    cnhFirstIssueAt: input.cnhFirstIssueAt || undefined,
    cnhPoints: input.cnhPoints,
    birthDate: input.birthDate || undefined,
    phone: input.phone || undefined,
    email: input.email || undefined,
    address: input.address || undefined,
    city: input.city || undefined,
    state: input.state || undefined,
    notes: input.notes || undefined,
    ...(isCreate && input.createUserAccount
      ? {
          createUserAccount: true,
          accessPassword: input.accessPassword,
        }
      : {}),
  };
}

export function useCreateDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DriverFormInput) => {
      const { data } = await api.post<{ data: Driver }>('/drivers', toApiPayload(input, true));
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.all });
    },
  });
}

export function useUpdateDriver(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DriverFormInput) => {
      const { data } = await api.patch<{ data: Driver }>(`/drivers/${id}`, toApiPayload(input, false));
      return data.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: KEY.all });
      qc.setQueryData(KEY.one(id), updated);
    },
  });
}

export function useCreateDriverAccess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email?: string; password: string }) => {
      const { data } = await api.post<{ data: Driver }>(`/drivers/${id}/access`, input);
      return data.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: KEY.all });
      qc.setQueryData(KEY.one(id), updated);
    },
  });
}

export function useDeleteDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/drivers/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.all });
    },
  });
}
